import type { Middleware } from 'openapi-fetch';

export interface CsrfMeta {
  cookieName: string;
  headerName: string;
}

export interface CsrfMiddlewareOptions {
  fetcher?: typeof fetch;
  csrfDiscoveryUrl?: string;
  /** Metadata TTL in ms (default 5 min). 0 disables caching. */
  ttlMs?: number;
  /**
   * Re-issue a CSRF-rejected mutating request once (see the retry note on
   * `createCsrfMiddlewareBundle`'s `onResponse`). Injected for tests; defaults
   * to a raw `fetch`, which re-sends the request OUTSIDE the openapi-fetch
   * middleware chain — so the retry can never re-enter this middleware and
   * loop.
   */
  reissue?: (request: Request) => Promise<Response>;
}

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Exact body the backend CSRF middleware emits for a missing or mismatched
 * token (`crates/sentinel-infra/src/middleware/csrf.rs` — both the
 * missing-cookie and token-mismatch branches). We key the retry on this
 * verbatim string so a benign 403 from a route handler (forbidden,
 * `step_up_required`, IP-policy) is never re-issued.
 */
export const CSRF_REJECTION_ERROR = 'CSRF token missing or invalid';

/**
 * How long a stashed request clone survives if neither `onResponse` nor
 * `onError` ever fires for its id (e.g. the request was aborted upstream before
 * the lifecycle returned through this middleware). 60s is far longer than any
 * real request budget, so it never fights a legitimate retry while still
 * bounding the clone map. Mirrors the same guard in `step-up.ts`.
 */
const REQUEST_CLONE_TTL_MS = 60_000;

function readCookie(name: string): string | null {
  for (const part of document.cookie.split(';')) {
    const [k, v] = part.trim().split('=');
    if (k === name && v) return decodeURIComponent(v);
  }
  return null;
}

/**
 * True only when `response` is the backend CSRF double-submit rejection.
 * Reads a clone so the original body stays available to the caller when we
 * decide not to retry; any parse failure (bodyless / non-JSON 403) is treated
 * as "not a CSRF rejection".
 */
async function isCsrfRejection(response: Response): Promise<boolean> {
  try {
    const body = (await response.clone().json()) as { error?: unknown };
    return body?.error === CSRF_REJECTION_ERROR;
  } catch {
    return false;
  }
}

/**
 * Pair of (middleware, applyToRequest) sharing the same CSRF meta cache.
 *
 * `applyToRequest` is exposed so other middlewares (notably step-up retry)
 * can re-apply a fresh CSRF header on a cloned Request before sending it via
 * raw `fetch`, without going through the full openapi-fetch middleware chain.
 *
 * Both halves read from the same cache, so when the response handler clears
 * the cache on a 403 (server-side rotation), the next `applyToRequest` call
 * will re-discover metadata and read the latest cookie.
 */
export interface CsrfMiddlewareBundle {
  middleware: Middleware;
  applyToRequest: (request: Request) => Promise<void>;
}

export function createCsrfMiddlewareBundle(
  opts: CsrfMiddlewareOptions = {},
): CsrfMiddlewareBundle {
  const fetcher = opts.fetcher ?? fetch;
  const reissue = opts.reissue ?? ((request: Request) => fetch(request));
  const url = opts.csrfDiscoveryUrl ?? '/auth/csrf';
  const ttlMs = opts.ttlMs ?? 5 * 60_000;
  let inflight: Promise<CsrfMeta> | null = null;
  let cached: { meta: CsrfMeta; expiresAt: number } | null = null;

  // Clones of in-flight mutating requests, keyed by openapi-fetch's stable
  // per-request id, so a CSRF-rejected mutation can be re-issued with a fresh
  // token. The original body is consumed once openapi-fetch fetches it, so we
  // keep a clone. Closure-scoped (not module-global) so separate bundle
  // instances never share ids. Each entry self-evicts after a generous TTL.
  interface CloneEntry {
    request: Request;
    timer: ReturnType<typeof setTimeout>;
  }
  const requestClones = new Map<string, CloneEntry>();

  function stashClone(id: string, request: Request): void {
    try {
      const clone = request.clone();
      // Drop any prior entry for this id first so its eviction timer can't be
      // orphaned (defensive — openapi-fetch ids are unique per in-flight
      // request, so this normally finds nothing).
      const prior = requestClones.get(id);
      if (prior) clearTimeout(prior.timer);
      const timer = setTimeout(() => requestClones.delete(id), REQUEST_CLONE_TTL_MS);
      // Best-effort: don't keep the Node event loop alive for this timer
      // (no-op in browsers / happy-dom).
      (timer as unknown as { unref?: () => void }).unref?.();
      requestClones.set(id, { request: clone, timer });
    } catch {
      // Non-cloneable request (rare) — skip; the retry simply won't fire.
    }
  }

  function takeClone(id: string): Request | undefined {
    const entry = requestClones.get(id);
    if (!entry) return undefined;
    clearTimeout(entry.timer);
    requestClones.delete(id);
    return entry.request;
  }

  async function fetchMeta(): Promise<CsrfMeta> {
    const r = await fetcher(url, { credentials: 'same-origin' });
    if (!r.ok) throw new Error(`csrf discovery failed: ${r.status}`);
    const json = (await r.json()) as { csrf_cookie_name?: string; header_name?: string };
    if (!json.csrf_cookie_name || !json.header_name) {
      throw new Error('csrf discovery returned malformed payload');
    }
    return { cookieName: json.csrf_cookie_name, headerName: json.header_name };
  }

  async function loadMeta(): Promise<CsrfMeta> {
    const now = Date.now();
    if (cached && cached.expiresAt > now) return cached.meta;
    if (inflight) return inflight;
    inflight = fetchMeta()
      .then((meta) => {
        cached = { meta, expiresAt: Date.now() + ttlMs };
        return meta;
      })
      .catch((err) => {
        cached = null;
        throw err;
      })
      .finally(() => {
        inflight = null;
      });
    return inflight;
  }

  async function applyToRequest(request: Request): Promise<void> {
    if (!MUTATION_METHODS.has(request.method)) return;
    let meta: CsrfMeta;
    try {
      meta = await loadMeta();
    } catch {
      // Discovery failure: leave request as-is; server will 403 legitimately.
      return;
    }
    // Clear any stale header before re-applying (the cookie may have rotated
    // out from under a prior copy).
    request.headers.delete(meta.headerName);
    const token = readCookie(meta.cookieName);
    if (token) request.headers.set(meta.headerName, token);
  }

  const middleware: Middleware = {
    async onRequest({ request, id }) {
      await applyToRequest(request);
      // Stash a clone of mutating requests so a CSRF-rejected one can be
      // re-issued once with a freshly read token (see onResponse). Safe
      // methods never 403 on CSRF and are never retried, so we skip them.
      if (id && MUTATION_METHODS.has(request.method)) stashClone(id, request);
      return request;
    },
    async onResponse({ response, id }) {
      // Always release any stashed clone for this request first.
      const stashed = id ? takeClone(id) : undefined;
      if (response.status !== 403) return undefined;
      // A CSRF double-submit rejection is the one 403 we can safely retry: the
      // backend rejects it BEFORE the route handler runs, so the mutation had
      // zero effect — re-issuing once is safe even for non-idempotent methods.
      // Any other 403 (forbidden, step_up_required, IP-policy) flows through.
      let retried: Response | undefined;
      if (stashed && (await isCsrfRejection(response))) {
        // The cookie name / header name are immutable server config; only the
        // cookie VALUE rotates (on the silent /auth/token refresh). So reuse
        // the cached meta and just re-read the now-settled cookie, applying a
        // fresh matching header to the clone. Re-issue exactly once via a raw
        // fetch; that fetch does not re-enter the openapi-fetch chain, so this
        // onResponse never fires again for the retry — there is no retry loop.
        await applyToRequest(stashed);
        try {
          retried = await reissue(stashed);
        } catch {
          // Network error on the retry — leave `retried` undefined so the
          // original 403 surfaces unchanged.
        }
      }
      // A 403 may indicate a rotated CSRF token/header — invalidate the meta
      // cache so the NEXT mutating call re-discovers. Kept unconditional (any
      // 403) to preserve prior behavior.
      cached = null;
      // `retried`: a 2xx heals the race; a second 403 (or any other status)
      // surfaces to the caller — we never retry twice. When undefined (not a
      // CSRF-403, or the retry threw) the original response flows through
      // unchanged.
      //
      // The retry re-sends via a raw fetch, and step-up and unauth have already
      // had their turn by the time this runs in the reverse onResponse order,
      // so neither gets to process the retried response. (The forbidden
      // middleware is registered ahead of this one and so runs after it — it
      // does see whatever we return here, which is the point of registering it
      // there.) Two consequences, both matching the existing accepted
      // limitation of step-up.ts's own replay (client.ts wires it as a raw
      // fetch too), and neither a regression versus not retrying:
      //  - A retried `step_up_required` 403 (only reachable for a step-up-gated
      //    mutation that also raced the CSRF rotation — never the step-up
      //    /verify endpoint this primarily fixes) is surfaced raw rather than
      //    opening the dialog; a subsequent, non-racing attempt still triggers
      //    step-up normally.
      //  - A retried 401 does not fire `sentinel:unauthenticated`. This is
      //    effectively unreachable: the /auth/token refresh that rotated the
      //    cookie just renewed the session, so the retry hits a valid one.
      return retried;
    },
    async onError({ id }) {
      // The fetch errored, so onResponse never fires for this id — release the
      // stashed clone now instead of leaning on the TTL (mirrors step-up.ts).
      if (id) takeClone(id);
      return undefined;
    },
  };

  return { middleware, applyToRequest };
}

export function createCsrfMiddleware(opts: CsrfMiddlewareOptions = {}): Middleware {
  return createCsrfMiddlewareBundle(opts).middleware;
}
